import React from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { RatingGraphic } from "../../../common/RatingGraphic";
import { UGCCarousel } from "../../../common/UGCCarousel";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IFormUUID, IReviewsProductTypeID } from "../../../models/nominals";
import { UGCListSubBlock } from "../../../models/ugc";
import { roundReviewRating } from "../../../utils/math";
import { ReviewsComponentTypes } from "../constants";
import { CarouselReviewList } from "../elements/CarouselReviewList";
import { LoadMoreButton } from "../elements/LoadMoreButton";
import { ReviewList } from "../elements/ReviewList";
import { ReviewStarRating } from "../elements/ReviewStarRating";
import { SidebarFilterSection } from "../elements/SidebarFilterSection";
import { TopBar } from "../elements/TopBar";
import { TopFilterSection } from "../elements/TopFilterSection";
import { WriteReviewButton } from "../elements/WriteReviewButton";
import { WriteReviewForm } from "../elements/WriteReviewForm";
import { WriteReviewThankYou } from "../elements/WriteReviewThankYou";

import styles from "./ProductReviews.module.scss";

const MAX_SEARCH_KEYWORDS_NUMBER = 6;

const buildSideNav = (
    displayedProductTypeIDWhitelist: IReviewsProductTypeID[],
    showProductsFilter: boolean,
    showSourcesFilter: boolean,
    starHasStroke: boolean | undefined,
    isCollapsedView: boolean | undefined,
): React.JSX.Element => {
    return (
        <SidebarFilterSection
            isMobile={false}
            displayedProductTypeIDWhitelist={displayedProductTypeIDWhitelist}
            showProducts={showProductsFilter}
            showSources={showSourcesFilter}
            starHasStroke={starHasStroke}
            writeReviewURL={""}
            onToggleMobileSideNav={() => {}}
            mobileSideNavOpen={true}
            showAppliedFilters={false}
            isCollapsedView={isCollapsedView}
        />
    );
};

const buildTopFilterNav = (
    displayedProductTypeIDWhitelist: IReviewsProductTypeID[],
    showProductsFilter: boolean,
    showSourcesFilter: boolean,
    reviewsComponentType: ReviewsComponentTypes,
): React.JSX.Element => {
    return (
        <TopFilterSection
            isMobile={false}
            displayedProductTypeIDWhitelist={displayedProductTypeIDWhitelist}
            showProducts={showProductsFilter}
            showSources={showSourcesFilter}
            maxKeywordsNumber={MAX_SEARCH_KEYWORDS_NUMBER}
            isCarouselComponentType={
                reviewsComponentType === ReviewsComponentTypes.CAROUSEL
            }
        />
    );
};

const buildReviews = (
    starHasStroke: boolean | undefined,
): React.JSX.Element => {
    return (
        <ReviewList
            showSideDate={true}
            enablePDPLinks={false}
            showRecommended={false}
            showVerboseUserInfo={false}
            starHasStroke={starHasStroke}
        />
    );
};

const buildLoadMoreButton = (onLoadMore: () => void): React.JSX.Element => {
    return <LoadMoreButton onLoadMore={onLoadMore} />;
};

const buildUGCCarousel = (
    ugcCarouselPosts: UGCListSubBlock[],
    ugcCarouselDesc: string,
): React.JSX.Element | null => {
    if (!ugcCarouselPosts || ugcCarouselPosts.length < 1) {
        return null;
    }

    return (
        <div
            role="region"
            aria-label="Testimonials Carousel"
            className="ugc-carousel-container ugc-carousel-container__reviews-full l-full-width"
        >
            {ugcCarouselDesc && (
                <section
                    className="ugc-carousel__header"
                    dangerouslySetInnerHTML={{
                        __html: ugcCarouselDesc,
                    }}
                />
            )}
            {ugcCarouselPosts.length > 0 && (
                <UGCCarousel tiles={ugcCarouselPosts} />
            )}
        </div>
    );
};

const buildRatingGraphic = (
    averageRating: number,
    starHasStroke: boolean | undefined,
): React.JSX.Element => {
    return (
        <div className="customer-review__stars customer-review__stars--mobile">
            <RatingGraphic
                cardClass="product-reviews-header"
                cardSize="large"
                rating={averageRating}
                onlyStars={true}
                starHasStroke={starHasStroke}
            />
        </div>
    );
};

const buildReviewList = (
    starHasStroke: boolean | undefined,
    onLoadMore: () => void,
): React.JSX.Element => {
    return (
        <div className={styles.reviewList}>
            {buildReviews(starHasStroke)}
            {buildLoadMoreButton(onLoadMore)}
        </div>
    );
};

const buildCarouselReviewList = (
    starHasStroke: boolean | undefined,
    onLoadMore: () => void,
): React.JSX.Element => {
    return (
        <CarouselReviewList
            showSideDate={true}
            enablePDPLinks={false}
            starHasStroke={starHasStroke}
            onLoadMore={onLoadMore}
            maxKeywordsNumber={MAX_SEARCH_KEYWORDS_NUMBER}
        />
    );
};

export const ProductReviews = (props: {
    displayedProductTypeIDWhitelist: IReviewsProductTypeID[];
    title: string;
    numReviews: number;
    averageRating: number;
    showWriteReviewButton: boolean;
    ugcCarouselDesc: string;
    ugcCarouselPosts: UGCListSubBlock[];
    brand: string;
    showProductsFilter: boolean;
    showSourcesFilter: boolean;
    writeReviewFormUUID: IFormUUID;
    writeReviewFormTitle: string;
    starHasStroke?: boolean;
    reviewsComponentType?: ReviewsComponentTypes;
    onLoadMore: () => void;
    isCollapsedView?: boolean;
}): React.ReactNode => {
    const titleRef = React.useRef<HTMLDivElement>(null);
    const [reviewsComponentType, setReviewsComponentType] =
        React.useState<ReviewsComponentTypes>(ReviewsComponentTypes.CLASSIC);

    useMountEffect(() => {
        if (props.reviewsComponentType) {
            setReviewsComponentType(props.reviewsComponentType);
        }
        const document = getDocument();
        if (!document) {
            return;
        }
        const scrollToReviews = document.body.getAttribute("scrollToReviews");
        const el = document.querySelector<HTMLElement>("#reviews__body");
        if (el && scrollToReviews) {
            el?.scrollIntoView(true);
            document.body.removeAttribute("scrollToReviews");
        }
    });

    const onReviewSubmit = (): void => {
        if (titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <div className="customer-review l-capped-width">
            <div className="customer-review__header customer-review__header--pdp">
                {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL &&
                    buildRatingGraphic(
                        props.averageRating,
                        props.starHasStroke,
                    )}
                {props.title && (
                    <h2 className={styles.title} ref={titleRef}>
                        {props.title}
                    </h2>
                )}
                {buildUGCCarousel(
                    props.ugcCarouselPosts,
                    props.ugcCarouselDesc,
                )}
                <ReviewStarRating
                    roundedRating={roundReviewRating(props.averageRating)}
                    numReviews={props.numReviews}
                    averageRating={props.averageRating}
                    starHasStroke={props.starHasStroke}
                    reviewsComponentType={reviewsComponentType}
                />
                {props.showWriteReviewButton && (
                    <>
                        <WriteReviewButton
                            formUUID={props.writeReviewFormUUID}
                        />
                        <WriteReviewForm
                            formUUID={props.writeReviewFormUUID}
                            formTitle={props.writeReviewFormTitle}
                            brand={props.brand}
                            onReviewSubmit={onReviewSubmit}
                        />
                        <WriteReviewThankYou
                            formUUID={props.writeReviewFormUUID}
                        />
                    </>
                )}
            </div>
            <div
                id="reviews__body"
                className="customer-review__body"
                tabIndex={-1}
            >
                {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL
                    ? buildSideNav(
                          props.displayedProductTypeIDWhitelist,
                          props.showProductsFilter,
                          props.showSourcesFilter,
                          props.starHasStroke,
                          props.isCollapsedView,
                      )
                    : buildTopFilterNav(
                          props.displayedProductTypeIDWhitelist,
                          props.showProductsFilter,
                          props.showSourcesFilter,
                          reviewsComponentType,
                      )}
                <div className={`${styles.content} customer-review__content`}>
                    <TopBar />
                    {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL
                        ? buildReviewList(props.starHasStroke, props.onLoadMore)
                        : buildCarouselReviewList(
                              props.starHasStroke,
                              props.onLoadMore,
                          )}
                </div>
            </div>
        </div>
    );
};
